import { useConnection } from '@nx/state';
import { useLazyGetCredentialsQuery } from '@nx/state/desktop';
import { isNotNullish, isNullish } from '@nx/stdlib';

import type { DbmsConnectionInfo } from './use-connection-form-modal';
import { useConnectionFormModal } from './use-connection-form-modal';

export const useDbmsConnection = (connectInfo: DbmsConnectionInfo, onConnectionSuccess?: () => void) => {
  const { connect, disconnect, status, metadata } = useConnection();
  const [getInstanceCredentials] = useLazyGetCredentialsQuery();
  const connectionModal = useConnectionFormModal({
    connectInfo,
    onConnectionReady: onConnectionSuccess,
    updateCredentialsOnSuccess: true,
  });

  const dbmsConnect = async () => {
    if (isNullish(connectInfo)) {
      throw new Error();
    }

    let savedUsername = '';
    let triedConnecting = false;
    try {
      const { data } = await getInstanceCredentials({ id: connectInfo.id, type: connectInfo.credentialsType });
      if (data === null || data === undefined) {
        throw new Error(`Credentials not found for instance ${connectInfo.id}`);
      }

      savedUsername = data.username;
      if (!data.password) {
        throw new Error(`Stored password not found for instance ${connectInfo.id}`);
      }

      triedConnecting = true;
      // Try to connect with the saved credentials
      const result = await connect({
        credentials: { type: 'basic', username: data.username },
        name: connectInfo.name,
        password: data.password,
        source: { type: 'desktop', sourceId: connectInfo.id },
        url: connectInfo.connectionUri,
      });

      if ('error' in result) {
        throw new Error();
      } else if (isNotNullish(onConnectionSuccess)) {
        onConnectionSuccess();
      }
    } catch {
      // We either had no credentials saved or the existing credentials didn't
      // work. Open the pre-filled connection modal and once we're successfully
      // connected save the new credentials.
      connectionModal.open({
        prefilledUsername: savedUsername,
        error: triedConnecting
          ? 'Connecting using saved credentials was unsuccessful. Connect using the correct credentials below.'
          : undefined,
      });
    }
  };

  const isCurrentConnection = metadata?.url === connectInfo.connectionUri && connectInfo.dbmsIsRunning;
  const isConnected = isCurrentConnection && status.isConnected;
  const isLoading = isCurrentConnection && (status.isConnecting || status.isDisconnecting);

  return {
    connect: () => {
      void dbmsConnect();
    },
    disconnect: () => {
      if (status.isConnected || status.isConnecting || status.isReconnecting) {
        void disconnect();
      }
    },
    openConnectWithCredentialsModal: () => {
      connectionModal.open();
    },

    /**
     * The current connection is targeting the selected instance, but we're not
     * necessarily connected yet.
     */
    isCurrentConnection,
    /** We're connected to the selected instance. */
    isConnected,
    /** We're either connecting or disconnecting to the selected instance. */
    isLoading,
  };
};
